import { ApplicantsMergeModal } from "@/pages/recruit/candidates/widgets/ApplicantsMergeModal.tsx";
import { useApplicantsSearch } from "@/queries/recruit/use-applicants.query.ts";
import { useEntityFormConfig } from "@/queries/use-custom-fields.query.ts";
import { ApiCFEntityTypes, ApiFieldTypes } from "@/types/custom-field.types.ts";
import {
  ApiApplicant,
  ApiApplicantFilter,
  ApiApplicantShort,
  ApplicantFilterDefault,
} from "@/types/recruit/applicant.types.ts";
import {
  Button,
  Modal,
  ModalProps,
  Select,
  SelectItem,
  Stack,
  Text,
  useModals,
} from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export interface ApplicantMergeWithModalProps extends ModalProps {
  applicant: ApiApplicant;
}

export function ApplicantMergeWithModal({ applicant, ...props }: ApplicantMergeWithModalProps) {
  const { t } = useTranslation();
  const { close, openModal } = useModals();
  const { hasField } = useEntityFormConfig(ApiCFEntityTypes.applicant);

  const [filter, setFilter] = useState<ApiApplicantFilter>({
    ...ApplicantFilterDefault,
    exclude_applicant_ids: [applicant.id],
  });
  const [selectedApplicant, setSelectedApplicant] = useState<ApiApplicantShort | null>(null);

  const { data: applicantsSearched, isLoading } = useApplicantsSearch(filter);

  const applicantOptions = useMemo(() => {
    if (!applicantsSearched) return [];
    return applicantsSearched.items.map((item): SelectItem => ({
      id: item.id,
      title: `${item.first_name} ${item.last_name}`,
      avatarUrl: item.photo_url,
      caption: [
        hasField(ApiFieldTypes.applicant_position) ? item.current_position.position : undefined,
        hasField(ApiFieldTypes.applicant_company) ? item.current_position.company : undefined,
      ]
        .filter(Boolean)
        .join(", "),
    }));
  }, [applicantsSearched, hasField]);

  const handleContinue = () => {
    if (!selectedApplicant) return;

    close(props.id);
    openModal(ApplicantsMergeModal, {
      applicant,
      similarApplicants: [selectedApplicant],
    });
  };

  return (
    <Modal
      {...props}
      title={t("ats:merge_with_candidate")}
      withCloser
      closeByEscEnabled
      actions={
        <Stack>
          <Button size={"lg"} disabled={!selectedApplicant} onClick={handleContinue}>
            {t("common:continue")}
          </Button>
        </Stack>
      }
    >
      <Stack gap={"lg"}>
        <Text className={"text-secondary"}>{t("ats:merge_with_description")}</Text>
        <Select
          label={t("ats:candidate")}
          emptyTitle={t("common:select")}
          isLoading={isLoading}
          value={selectedApplicant?.id}
          searchable
          onSearchOptions={(text) => setFilter((prev) => ({ ...prev, text }))}
          showAvatar
          showCaption
          loadOptionsOnOpen
          searchOptions={applicantOptions}
          onChange={(value) =>
            setSelectedApplicant(
              applicantsSearched?.items.find((item) => item.id === Number(value)) ?? null
            )
          }
        />
      </Stack>
    </Modal>
  );
}
